import { AudioRegionBox, NoteEventCollectionBox, NoteRegionBox, TrackBox, ValueEventCollectionBox, ValueRegionBox } from "@opendaw/studio-boxes";
import { PPQN, TimeBase } from "@opendaw/lib-dsp";
import { ColorCodes, TrackType } from "@opendaw/studio-adapters";
import { asInstanceOf, isDefined, panic, UUID } from "@opendaw/lib-std";
import { Facade, Props } from "../Common";
import { Guard } from "../Guard";
import { NoteEvents, ValueEvents } from "./Events";
import { AudioContents } from "./AudioContents";
import { AudioFiles } from "../AudioFiles";
import { TrackImpls } from "./Tracks";
import { NoteClipImpl, ValueClipImpl } from "./Clips";
export class RegionFacade extends Facade {
    constructor(context, box) {
        super(context, box);
        this.bind({
            position: box.position, duration: box.duration, mute: box.mute, label: box.label, hue: box.hue,
            loopDuration: box.loopDuration, loopOffset: box.loopOffset
        });
    }
    get complete() { return this.position + this.duration; }
    get trackBox() {
        return asInstanceOf(this.box.regions.targetVertex.unwrap("region has no track").box, TrackBox);
    }
}
export class NoteRegionImpl extends RegionFacade {
    static wrap(context, box) {
        return context.facade(box, () => new NoteRegionImpl(context, box));
    }
    constructor(context, box) { super(context, box); }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get noteEvents() {
        return new NoteEvents(this.context, asInstanceOf(this.box.events.targetVertex.unwrap("region has no events").box, NoteEventCollectionBox));
    }
    get events() { return this.noteEvents.list(); }
    addEvent(props) { return this.noteEvents.add(props); }
    addEvents(events) { return this.noteEvents.addAll(events); }
    clearEvents() { this.noteEvents.clear(); }
}
export class ValueRegionImpl extends RegionFacade {
    static wrap(context, box) {
        return context.facade(box, () => new ValueRegionImpl(context, box));
    }
    constructor(context, box) { super(context, box); }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get valueEvents() {
        return new ValueEvents(this.context, asInstanceOf(this.box.events.targetVertex.unwrap("region has no events").box, ValueEventCollectionBox));
    }
    get events() { return this.valueEvents.list(); }
    addEvent(props) { return this.valueEvents.add(props); }
    addEvents(events) { return this.valueEvents.addAll(events); }
    clearEvents() { this.valueEvents.clear(); }
}
export class AudioRegionImpl extends RegionFacade {
    static wrap(context, box) {
        return context.facade(box, () => new AudioRegionImpl(context, box));
    }
    constructor(context, box) {
        super(context, box);
        this.bind({
            gain: box.gain, waveformOffset: box.waveformOffset,
            fading: { in: box.fading.in, out: box.fading.out, inSlope: box.fading.inSlope, outSlope: box.fading.outSlope }
        });
    }
    get track() { return TrackImpls.wrap(this.context, this.trackBox); }
    get sample() { return AudioContents.sample(this.context, this.box); }
    get playback() { return AudioContents.playback(this.box); }
    get transientPlayMode() { return AudioContents.transientPlayMode(this.box); }
    set transientPlayMode(value) { AudioContents.setTransientPlayMode(this.context, this.box, value); }
    get playbackRate() { return AudioContents.playbackRate(this.box); }
    set playbackRate(value) { AudioContents.setPlaybackRate(this.context, this.box, value); }
    get transpose() { return AudioContents.transpose(this.box); }
    set transpose(value) { AudioContents.setTranspose(this.context, this.box, value); }
}
export var Regions;
(function (Regions) {
    Regions.wrap = (context, box) => {
        if (box instanceof NoteRegionBox) {
            return NoteRegionImpl.wrap(context, box);
        }
        if (box instanceof AudioRegionBox) {
            return AudioRegionImpl.wrap(context, box);
        }
        if (box instanceof ValueRegionBox) {
            return ValueRegionImpl.wrap(context, box);
        }
        return panic(`${box.name} is not a region`);
    };
    Regions.list = (context, trackBox) => trackBox.regions.pointerHub.incoming()
        .map(({ box }) => Regions.wrap(context, box))
        .sort((a, b) => a.position - b.position);
    Regions.assertNoOverlap = (context, trackBox, position, duration, timeBase) => {
        if (timeBase === TimeBase.Seconds) {
            return;
        }
        const complete = position + duration;
        Regions.list(context, trackBox).forEach(region => {
            if (region instanceof AudioRegionImpl && region.box.timeBase.getValue() === TimeBase.Seconds) {
                return;
            }
            if (region.position < complete && region.complete > position) {
                return panic(new RangeError(`Region [${position}, ${complete}] overlaps existing region '${region.label}' [${region.position}, ${region.complete}]`));
            }
        });
    };
    Regions.validateTrackType = (trackBox, expected, what) => {
        if (trackBox.type.getValue() !== expected) {
            return panic(new TypeError(`Cannot add ${what} to a ${TrackType.toLabelString(trackBox.type.getValue())} track`));
        }
    };
    const mirrorCollection = (mirror) => {
        if (mirror instanceof NoteRegionImpl || mirror instanceof NoteClipImpl) {
            return mirror.noteEvents.collection;
        }
        if (mirror instanceof ValueRegionImpl || mirror instanceof ValueClipImpl) {
            return mirror.valueEvents.collection;
        }
        return panic(new TypeError(`mirror: expected a region or clip of the same type, got ${Guard.describe(mirror)}`));
    };
    const checkMirrorType = (mirror, expected) => {
        const isNote = mirror instanceof NoteRegionImpl || mirror instanceof NoteClipImpl;
        const isValue = mirror instanceof ValueRegionImpl || mirror instanceof ValueClipImpl;
        if ((expected === TrackType.Notes && !isNote) || (expected === TrackType.Value && !isValue)) {
            return panic(new TypeError("mirror: region or clip type does not match the track"));
        }
    };
    Regions.createNoteRegion = (context, trackBox, props) => {
        Regions.validateTrackType(trackBox, TrackType.Notes, "a note region");
        return context.edit(() => {
            const position = Guard.int32("any", props?.position ?? 0, "position");
            const duration = Guard.int32("positive", props?.duration ?? PPQN.Bar, "duration");
            Regions.assertNoOverlap(context, trackBox, position, duration, TimeBase.Musical);
            const mirror = props?.mirror;
            if (isDefined(mirror)) {
                checkMirrorType(mirror, TrackType.Notes);
            }
            const collection = isDefined(mirror)
                ? mirrorCollection(mirror)
                : NoteEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = NoteRegionBox.create(context.boxGraph, UUID.generate(), box => {
                box.position.setValue(position);
                box.duration.setValue(duration);
                box.loopDuration.setValue(duration);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Notes));
                box.regions.refer(trackBox.regions);
                box.events.refer(collection.owners);
            });
            const rest = Props.without(props, "mirror", "position", "duration");
            return Props.apply(NoteRegionImpl.wrap(context, box), rest);
        });
    };
    Regions.createValueRegion = (context, trackBox, props) => {
        Regions.validateTrackType(trackBox, TrackType.Value, "an automation region");
        return context.edit(() => {
            const position = Guard.int32("any", props?.position ?? 0, "position");
            const duration = Guard.int32("positive", props?.duration ?? PPQN.Bar, "duration");
            Regions.assertNoOverlap(context, trackBox, position, duration, TimeBase.Musical);
            const mirror = props?.mirror;
            if (isDefined(mirror)) {
                checkMirrorType(mirror, TrackType.Value);
            }
            const collection = isDefined(mirror)
                ? mirrorCollection(mirror)
                : ValueEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = ValueRegionBox.create(context.boxGraph, UUID.generate(), box => {
                box.position.setValue(position);
                box.duration.setValue(duration);
                box.loopDuration.setValue(duration);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Value));
                box.regions.refer(trackBox.regions);
                box.events.refer(collection.owners);
            });
            const rest = Props.without(props, "mirror", "position", "duration");
            return Props.apply(ValueRegionImpl.wrap(context, box), rest);
        });
    };
    Regions.createAudioRegion = (context, trackBox, sample, props) => {
        Regions.validateTrackType(trackBox, TrackType.Audio, "an audio region");
        return context.edit(() => {
            const fileBox = AudioFiles.use(context, sample);
            const validated = AudioFiles.validate(sample);
            const playback = isDefined(props?.playback) ? AudioContents.validatePlayback(props.playback) : AudioContents.defaultPlayback(validated);
            const projectBpm = context.skeleton.mandatoryBoxes.timelineBox.bpm.getValue();
            const position = Guard.int32("any", props?.position ?? 0, "position");
            const duration = Guard.float32("positive", props?.duration ?? AudioContents.defaultDuration(validated, playback, projectBpm), "duration");
            const timeBase = playback === "no-sync" ? TimeBase.Seconds : TimeBase.Musical;
            Regions.assertNoOverlap(context, trackBox, position, duration, timeBase);
            const playMode = AudioContents.createPlayMode(context, playback, duration, validated.duration, {
                transientPlayMode: props?.transientPlayMode, playbackRate: props?.playbackRate, transpose: props?.transpose
            });
            const collection = ValueEventCollectionBox.create(context.boxGraph, UUID.generate());
            const box = AudioRegionBox.create(context.boxGraph, UUID.generate(), box => {
                box.position.setValue(position);
                box.duration.setValue(duration);
                box.loopDuration.setValue(duration);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Audio));
                box.label.setValue(validated.name);
                box.regions.refer(trackBox.regions);
                box.file.refer(fileBox);
                box.events.refer(collection.owners);
                box.timeBase.setValue(timeBase);
                if (isDefined(playMode)) {
                    box.playMode.refer(playMode);
                }
            });
            const rest = Props.without(props, "playback", "position", "duration", "transientPlayMode", "playbackRate", "transpose");
            return Props.apply(AudioRegionImpl.wrap(context, box), rest);
        });
    };
    Regions.forEachRegion = (context, procedure) => context.boxGraph.boxes().forEach(box => {
        if (box instanceof NoteRegionBox || box instanceof AudioRegionBox || box instanceof ValueRegionBox) {
            procedure(Regions.wrap(context, box));
        }
    });
})(Regions || (Regions = {}));
